import { ReactNode } from 'react';

import { User } from 'entities';

import { GiftCard, MoneyBag } from 'components/DecorativeIcons';

import { AnyObject } from 'types';
import { REGEX } from 'utils/constants';
import { listGet } from 'utils/array';

function splitFullName(name: string) {
  const [firstName, ...lastName] = name.split(' ').filter(Boolean);

  return {
    firstName,
    lastName: lastName.join(' '),
  };
}

function validateFullName(name: string) {
  const valid = REGEX.NAME().test(name);

  if (!valid) return 'validation.full-name';
}

type RewardConfig = {
  name: string;
  id: string;
  selectLabel: string;
  icon: ReactNode;
  description: string | Record<string, string>;
  value: Record<string, string>;
  points: number;

  alert: string;

  formEntries: Array<{
    name: string;
    disabled?: boolean;
    getDefaultValue: (user: User) => string;
    label: string;
    transform?: (value: string) => AnyObject;
    validate?: (value: string, form: Record<string, string>) => string | null | void | undefined;
  }>;
};

const rewards: RewardConfig[] = [
  {
    name: 'PayPal',
    description: 'points-to-money',
    id: 'paypal',
    selectLabel: 'Paypal',
    icon: <MoneyBag />,
    points: 2000,
    alert: 'paypal-alert',
    value: {
      AR: '20 USD',
      BR: 'R$ 100',
      CL: '20 USD',
      CO: '20 USD',
      CR: '20 USD',
      PA: '20 USD',
      PE: '20 USD',
      PR: '20 USD',
      US: '20 USD',
      UY: '20 USD',
      MX: '400 MXN',
      ES: '20 EUR',
    },
    formEntries: [
      {
        getDefaultValue: ({ email }) => email,
        label: 'email',
        name: 'email',
        validate: (email) => {
          const valid = REGEX.EMAIL().test(email.trim());

          if (!valid) return 'validation.email';
        },
      },
      {
        getDefaultValue: ({ email }) => email,
        label: 'email-confirm',
        name: 'emailConfirmation',
        validate: (confirmation, { email }) => {
          const valid = REGEX.EMAIL().test(confirmation.trim());

          if (!valid) return 'validation.email';

          if (email.trim() !== confirmation.trim()) return 'validation.email-match';
        },
      },
    ],
  },
  {
    name: 'Tremendous',
    icon: <MoneyBag />,
    points: 2000,
    id: 'tremendous',
    description: 'points-to-virtual-card',
    selectLabel: 'virtual-card',
    alert: 'tremendous-alert',
    value: {
      AR: '20 USD',
      BO: '20 USD',
      BR: 'R$ 100',
      CL: '20 USD',
      CO: '20 USD',
      CR: '20 USD',
      DR: '20 USD',
      EC: '20 USD',
      GT: '20 USD',
      HN: '20 USD',
      PA: '20 USD',
      PE: '20 USD',
      PR: '20 USD',
      PY: '20 USD',
      SV: '20 USD',
      US: '20 USD',
      UY: '20 USD',
      MX: '400 MXN',
      ES: '20 EUR',
    },

    formEntries: [
      {
        getDefaultValue: ({ firstName, lastName }) => `${firstName} ${lastName}`,
        label: 'full-name',
        name: 'fullName',
        transform: splitFullName,
        validate: validateFullName,
      },
      {
        getDefaultValue: ({ email }) => email,
        label: 'email',
        name: 'email',
        disabled: true,
      },
    ],
  },
  {
    name: 'gift-card',
    icon: <GiftCard />,
    points: 2000,
    id: 'tango',
    selectLabel: 'gift-card',
    alert: 'gift-card-alert',
    description: {
      BR: 'Crédito em lojas como Spotify, Netflix, Uber etc.',
      CO: 'Crédito en tiendas como Falabella, Jumbo, PlayStation, Xbox, Spotify.',
      PE: 'Crédito en tiendas como Metro, Prüne, Mr. Sushi, CHIFA.',
      MX: 'Crédito en tiendas como Uber, Google Play, Xbox, Dinero Electrónico Coppel, Spotify, Netflix.',
    },
    value: {
      BR: 'R$ 100',
      CO: '81000 COP',
      PE: '74 PEN',
      MX: '400 MXN',
    },
    formEntries: [
      {
        getDefaultValue: ({ firstName, lastName }) => `${firstName} ${lastName}`,
        label: 'full-name',
        name: 'fullName',
        transform: splitFullName,
        validate: validateFullName,
      },
      {
        getDefaultValue: ({ email }) => email,
        label: 'email',
        name: 'email',
        disabled: true,
      },
    ],
  },
  {
    name: 'Western Union',
    description: 'points-to-money',
    icon: <MoneyBag />,
    id: 'westernUnion',
    selectLabel: 'Western Union',
    points: 2000,
    alert: 'wu-alert',
    value: {
      VE: '20 USD',
      NI: '20 USD',
    },
    formEntries: [
      {
        getDefaultValue: ({ firstName, lastName }) => `${firstName} ${lastName}`,
        label: 'full-name',
        name: 'fullName',
        transform: splitFullName,
        validate: validateFullName,
      },
    ],
  },
];

export function getAvailableRewards(country: string) {
  return rewards.filter((r) => r.value[country]);
}

export function transformRewardPayload(reward: string, form: Record<string, string>) {
  const config = listGet(rewards, reward);

  if (!config) return {};

  const { formEntries } = config;

  const result = formEntries.reduce((acc, next) => {
    const { transform, name } = next;

    const current = transform?.(form[name]) ?? { [name]: form[name] };

    return {
      ...acc,
      ...current,
    };
  }, {});

  return result;
}
